import React, { useCallback, useEffect, useState } from "react"
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
} from "@mui/material"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Paper from "@mui/material/Paper"
import { handlePromise } from "../../../../utils/handlePromisse"
import {
    ExperimentResults,
    getExperimentResults,
} from "../../../../services/ai-experiment.service"

type Props = {
    open: boolean
    experimentId: number | null
    onClose: () => void
    onError: (message: string) => void
}

const usd = (n: number) => `$${n.toFixed(n < 0.01 ? 6 : 4)}`

export const ResultsDialog: React.FC<Props> = ({ open, experimentId, onClose, onError }) => {
    const [results, setResults] = useState<ExperimentResults | null>(null)
    const [loading, setLoading] = useState(false)

    const fetchResults = useCallback(async () => {
        if (!experimentId) return
        setLoading(true)
        const [result, error] = await handlePromise(getExperimentResults(experimentId))
        setLoading(false)
        if (error) {
            onError(error.response?.data?.message || "Erro ao carregar resultados")
            return
        }
        setResults(result.data)
    }, [experimentId, onError])

    useEffect(() => {
        if (open && experimentId) {
            setResults(null)
            fetchResults()
        }
    }, [open, experimentId, fetchResults])

    const hasData = results && results.rounds.length > 0

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                Resultados — {results?.experiment.name ?? ""}
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : !hasData ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }} align="center">
                        Ainda não há mensagens registradas para este experimento.
                    </Typography>
                ) : (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Comparação por rodada e braço. "% sem tool" indica respostas geradas sem
                            consultar o banco — quanto menor, mais o modelo aderiu ao RAG. Rating é o
                            feedback das usuárias (1 a 3).
                        </Typography>

                        {results!.rounds.map((round) => (
                            <Box key={round.round} sx={{ mb: 3 }}>
                                <Chip
                                    label={`Round ${round.round}${round.round % 2 === 0 ? " (crossover)" : ""}`}
                                    color="primary"
                                    size="small"
                                    sx={{ mb: 1 }}
                                />
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Braço</TableCell>
                                                <TableCell align="right">Mensagens</TableCell>
                                                <TableCell align="right">Usuárias</TableCell>
                                                <TableCell align="right">Msg/usuária</TableCell>
                                                <TableCell align="right">Custo total</TableCell>
                                                <TableCell align="right">Custo/usuária</TableCell>
                                                <TableCell align="right">Latência média</TableCell>
                                                <TableCell align="right">Tool calls (méd.)</TableCell>
                                                <TableCell align="right">% sem tool</TableCell>
                                                <TableCell align="right">Rating médio</TableCell>
                                                <TableCell align="right">Nº feedback</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {round.arms.map((arm) => (
                                                <TableRow hover key={arm.provider}>
                                                    <TableCell>
                                                        <Chip size="small" variant="outlined" label={arm.provider} />
                                                    </TableCell>
                                                    <TableCell align="right">{arm.messages}</TableCell>
                                                    <TableCell align="right">{arm.activeUsers}</TableCell>
                                                    <TableCell align="right">{arm.messagesPerUser}</TableCell>
                                                    <TableCell align="right">{usd(arm.totalCostUsd)}</TableCell>
                                                    <TableCell align="right">{usd(arm.costPerUserUsd)}</TableCell>
                                                    <TableCell align="right">{arm.avgLatencyMs} ms</TableCell>
                                                    <TableCell align="right">{arm.avgToolCalls}</TableCell>
                                                    <TableCell align="right">{arm.zeroToolPct}%</TableCell>
                                                    <TableCell align="right">
                                                        {arm.avgRating != null ? `${arm.avgRating} / 3` : "—"}
                                                    </TableCell>
                                                    <TableCell align="right">{arm.feedbackCount}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    )
}
